/* date functions */

import {Temporal} from "@js-temporal/polyfill";
import {RunningEntry} from "@/shared/timr";
import {DurationFormat, RunningTimeStatus, VacationUnit} from "@/shared/timrTypes";

export {Temporal} from "@js-temporal/polyfill";

const ISO_DATE_FORMAT = "YYYY-MM-DD";

// ---- Time-input normalisation ----

/**
 * Normalize free-form time input written by the user in a JQuery field.
 *
 * Accepts numeric shortcuts (e.g. "8" → "08:00", "830" → "08:30"), AM/PM suffixes,
 * and `HH:mm` forms. Re-formats the field value in place using `globals.timeFormat`.
 */
export function normalizeTimeInput(field: JQuery): void {
	let fieldValue = field.val() as string;
	if (fieldValue) {
		const isAM = fieldValue.match(/am/i);
		const isPM = fieldValue.match(/pm/i);
		fieldValue = fieldValue.replace(/am/i, "");
		fieldValue = fieldValue.replace(/pm/i, "");
		fieldValue = $.trim(fieldValue);
		if (fieldValue.indexOf(":") !== -1) {
			const hoursAndMinutes = fieldValue.split(":");
			fieldValue = (hoursAndMinutes[0].length < 2 ? "0" : "") + hoursAndMinutes[0] + ":" + (hoursAndMinutes[1].length < 2 ? "0" : "") + (hoursAndMinutes[1] ? hoursAndMinutes[1] : "0");
		} else {
			const fieldValueParsed = parseInt(fieldValue, 10);
			if (fieldValueParsed < 24 && fieldValueParsed !== 0) {
				fieldValue = (fieldValueParsed < 10 ? "0" : "") + fieldValue + ":00";
			} else if (fieldValueParsed >= 100) {
				const hours = wholeNumber(fieldValueParsed / 100);
				const minutes = fieldValueParsed - hours * 100;
				fieldValue = (hours < 10 ? "0" : "") + hours + ":" + (minutes < 10 ? "0" : "") + minutes;
			} else if (fieldValueParsed === 0) {
				fieldValue = "00:00";
			} else if (fieldValueParsed) {
				fieldValue = "00:" + (fieldValue.length < 2 ? "0" : "") + fieldValue;
			}
		}
		if (isAM) {
			fieldValue = fieldValue + " AM";
		} else if (isPM) {
			fieldValue = fieldValue + " PM";
		}
	}
	if (fieldValue) {
		const parsedTime = parseTimeInput(fieldValue);
		if (parsedTime) {
			field.val(formatTime(parsedTime));
		}
	}
}

/**
 * Returns the start (00:00) of the configured week for the given date.
 * The configured first day is read from globals.firstDayOfWeek.
 */
export function getStartOfConfiguredWeek(date: Temporal.PlainDate): Temporal.PlainDate {
	const dayOffset = (date.dayOfWeek - globals.firstDayOfWeek + 7) % 7;
	return date.subtract({days: dayOffset});
}

/**
 * Returns the first day of the calendar quarter the given date falls in
 * (1 Jan, 1 Apr, 1 Jul, or 1 Oct).
 */
export function getStartOfQuarter(date: Temporal.PlainDate): Temporal.PlainDate {
	const quarterStartMonth = date.month - ((date.month - 1) % 3);
	return date.with({month: quarterStartMonth, day: 1});
}

// ---- Parse functions ----

/**
 * Compose locale-formatted date, time, and timezone-offset inputs into a Temporal.ZonedDateTime.
 *
 * The date is parsed per `globals.dateFormatForParsing`, the time per `globals.timeFormatForParsing`,
 * and the timezone is an "+HH:MM" / "-HH:MM" / "Z" offset string. Moment is used internally for
 * locale-aware parsing; the result is a Temporal type with seconds zeroed.
 */
export function parseDateTimeZone(date: string, time: string, timezone: string): Temporal.ZonedDateTime | null {
	if (date && time) {
		const m = moment.parseZone(
			`${date} ${time} ${timezone}`,
			`${globals.dateFormatForParsing} ${globals.timeFormatForParsing} Z`
		);
		if (m.isValid()) {
			try {
				const offset = m.format("Z"); // moment's "Z" token: "+HH:MM" (always offset form, never literal "Z")
				return Temporal.PlainDateTime.from(m.format("YYYY-MM-DDTHH:mm:00")).toZonedDateTime(offset);
			} catch {
				return null;
			}
		}
	}
	return null;
}

/**
 * Parse a locale-formatted date string (per `globals.dateFormatForParsing`) into a Temporal.PlainDate.
 * Only use this for strings the user typed or that are already in the locale format.
 */
export function parseDateInput(date: string): Temporal.PlainDate | null {
	if (date) {
		const m = moment.utc(date, globals.dateFormatForParsing);
		if (m.isValid()) {
			try {
				return Temporal.PlainDate.from({year: m.year(), month: m.month() + 1, day: m.date()});
			} catch {
				return null;
			}
		}
	}
	return null;
}

/**
 * Convert a FullCalendar/moment date boundary value into Temporal.PlainDate.
 * This is intended for legacy library boundaries only.
 */
export function momentToPlainDate(value: moment.Moment): Temporal.PlainDate {
	return Temporal.PlainDate.from({
		year: value.year(),
		month: value.month() + 1,
		day: value.date(),
	});
}

/**
 * Parse a free-form locale time string into a Temporal.PlainTime.
 *
 * Accepts the locale format (`globals.timeFormatForParsing`) and numeric shortcuts
 * for users who type without a separator: a single hour ("8" → 08:00),
 * minutes only ("45" → 00:45), and HHMM ("830" → 08:30).
 */
export function parseTimeInput(time: string): Temporal.PlainTime | null {
	if (time) {
		if (time.indexOf(":") === -1) {
			const duration = parseInt(time, 10);
			// Non-numeric or negative input would feed NaN/out-of-range values into
			// Temporal.PlainTime.from and throw; treat it as no value instead.
			if (isNaN(duration) || duration < 0) {
				return null;
			}
			let hour: number, minute: number;
			if (duration < 24) {
				[hour, minute] = [duration, 0];
			} else if (duration < 60) {
				[hour, minute] = [0, duration];
			} else if (duration % 100 >= 60) {
				// Total-minutes shortcut for inputs whose last two digits would be invalid as HHMM.
				// e.g. "75" → 01:15, "165" → 02:45, "275" → 04:35. Wrap mod 24h for >1440min inputs.
				hour = Math.floor(duration / 60) % 24;
				minute = duration % 60;
			} else {
				// HHMM shortcut, e.g. "830" → 08:30, "1234" → 12:34. Wrap hour mod 24 so
				// "2400" → 00:00 and "2500" → 01:00 (matching the prior Date overflow behaviour).
				minute = duration % 100;
				hour = Math.floor(duration / 100) % 24;
			}
			return Temporal.PlainTime.from({hour, minute, second: 0});
		} else {
			const parsedTime = moment.utc(time, globals.timeFormatForParsing);
			if (parsedTime.isValid()) {
				return Temporal.PlainTime.from({hour: parsedTime.hour(), minute: parsedTime.minute(), second: 0});
			}
		}
	}
	return null;
}

// ------- API parsing and formatting ---------

/**
 * Parse an ISO offset-datetime string (e.g., "2024-01-15T10:30:00+01:00") into a
 * Temporal.ZonedDateTime with the offset preserved as the timezone.
 *
 * Replaces moment.parseZone — the resulting ZonedDateTime renders times in the
 * server-supplied offset zone, which is the per-user timezone rendering contract.
 */
export function parseISOZonedDateTime(dateString: string | null | undefined): Temporal.ZonedDateTime | null {
	if (dateString) {
		try {
			// Temporal.ZonedDateTime.from requires a [bracketed] timezone, so duplicate the
			// trailing ISO offset into the bracket. 'Z' must be spelled 'UTC' inside the bracket.
			const withBracket = dateString.replace(/(Z|[+-]\d{2}:\d{2})$/, m => `${m}[${m === 'Z' ? 'UTC' : m}]`);
			return Temporal.ZonedDateTime.from(withBracket).with({second: 0, millisecond: 0, microsecond: 0, nanosecond: 0});
		} catch {
			return null;
		}
	}
	return null;
}

/**
 * Parse an offset-less ISO datetime string ("YYYY-MM-DDTHH:mm[:ss]") into a
 * Temporal.PlainDateTime. The "Plain" half of the parseISO*DateTime pair —
 * use this when the wire string carries no zone information. Returns null on
 * empty/invalid input. Seconds are zeroed.
 */
export function parseISOPlainDateTime(dateString: string | null | undefined): Temporal.PlainDateTime | null {
	if (dateString) {
		try {
			return Temporal.PlainDateTime.from(dateString).with({second: 0, millisecond: 0, microsecond: 0, nanosecond: 0});
		} catch {
			return null;
		}
	}
	return null;
}

/** Parse an ISO date string ("YYYY-MM-DD") into a Temporal.PlainDate. Returns null on empty/invalid input. */
export function parseISODate(date: string | null | undefined): Temporal.PlainDate | null {
	if (date) {
		try {
			return Temporal.PlainDate.from(date);
		} catch {
			return null;
		}
	}
	return null;
}

/**
 * Parse an ISO time string ("HH:mm:ss" or "HH:mm") into a Temporal.PlainTime.
 * Returns null on empty/invalid input. Seconds are zeroed.
 */
export function parseISOTime(timeString: string | null | undefined): Temporal.PlainTime | null {
	if (timeString) {
		try {
			return Temporal.PlainTime.from(timeString).with({second: 0, millisecond: 0, microsecond: 0, nanosecond: 0});
		} catch {
			return null;
		}
	}
	return null;
}

/**
 * Parse a backend ISO instant string into a Temporal.Instant, or null when the
 * value is empty/null. Present values are trusted to be valid instants, so this
 * only preserves the nullable contract (no malformed-input guard).
 */
export function parseInstant(instant: string | null | undefined): Temporal.Instant | null {
	return instant ? Temporal.Instant.from(instant) : null;
}

/** Serialize a date(time) as the canonical wire-format ISO date string ("YYYY-MM-DD"). */
export function getISODate(date: Temporal.PlainDate | Temporal.ZonedDateTime | null | undefined): string {
	if (!date) return "";
	const pd = date instanceof Temporal.ZonedDateTime ? date.toPlainDate() : date;
	return pd.toString();
}

/** Serialize a time(date) as the canonical wire-format ISO time string ("HH:mm:ss", matching the backend LocalTime format). */
export function getISOTime(time: Temporal.PlainTime | Temporal.ZonedDateTime | null | undefined): string {
	if (!time) return "";
	const pt = time instanceof Temporal.PlainTime ? time : time.toPlainTime();
	return pt.toString({smallestUnit: 'second'});
}

/**
 * Serialize a ZonedDateTime as a wire-format ISO datetime string preserving the source offset
 * with millisecond precision (e.g. "2023-07-25T10:00:00.000+02:00"). Inverse of `parseISOZonedDateTime`.
 * `timeZoneName: 'never'` strips the trailing IANA bracket annotation.
 */
export function getISOZonedDateTime(date: Temporal.ZonedDateTime | null | undefined): string {
	if (!date) return "";
	return date.toString({timeZoneName: "never", fractionalSecondDigits: 3});
}

/** Current system UTC offset, e.g. "+02:00". For backend `localTimeZone` form params. */
export function getCurrentISOOffset(): string {
	return Temporal.Now.zonedDateTimeISO().offset;
}

// ---- Format functions ----

export function formatDate(date: Temporal.PlainDate | Temporal.ZonedDateTime | null | undefined): string {
	if (!date) return "";
	const pd = date instanceof Temporal.ZonedDateTime ? date.toPlainDate() : date;
	return moment.utc(pd.toString(), ISO_DATE_FORMAT).format(globals.dateFormat);
}

export function formatMonth(date: Temporal.PlainDate | Temporal.ZonedDateTime | null | undefined): string {
	if (!date) return "";
	const pd = date instanceof Temporal.ZonedDateTime ? date.toPlainDate() : date;
	// Temporal months are 1-indexed; globals.monthNames is 0-indexed (January = 0)
	return `${globals.monthNames[pd.month - 1]} ${pd.year}`;
}

export function formatDateWithoutYear(date: Temporal.PlainDate | Temporal.ZonedDateTime | null | undefined): string {
	if (!date) return "";
	const pd = date instanceof Temporal.ZonedDateTime ? date.toPlainDate() : date;
	return moment.utc(pd.toString(), ISO_DATE_FORMAT).format(globals.dateFormatWithoutYear);
}

export function formatTime(date: Temporal.PlainTime | Temporal.ZonedDateTime | Temporal.PlainDateTime | null | undefined): string {
	if (!date) return "";
	let pt: Temporal.PlainTime;
	if (date instanceof Temporal.PlainTime) {
		pt = date;
	} else {
		pt = date.toPlainTime();
	}
	return moment.utc(pt.toString({smallestUnit: 'minute'}), 'HH:mm').format(globals.timeFormat);
}

export function formatDateTime(date: Temporal.ZonedDateTime | Temporal.PlainDateTime | null | undefined): string {
	if (!date) return "";
	const mom = date instanceof Temporal.ZonedDateTime
		? moment.parseZone(date.toString({timeZoneName: "never"}))
		: moment(date.toString({smallestUnit: 'minute'}), 'YYYY-MM-DDTHH:mm');

	return mom.format(globals.dateTimeFormat);
}

/**
 * Formats the date as a day of the week, e.g. "Mon"
 */
export function formatDateDay(date: Temporal.PlainDate | Temporal.ZonedDateTime | null | undefined): string {
	if (!date) return "";
	const pd = date instanceof Temporal.ZonedDateTime ? date.toPlainDate() : date;
	const dateString = moment.utc(pd.toString(), ISO_DATE_FORMAT).format("dd");
	return dateString[0].toUpperCase() + dateString.slice(1);
}

// ---- Number / duration / currency helpers (unchanged) ----

export function formatDurationDecimal(durationInMilliseconds: number): string {
	const durationHours = wholeNumber(durationInMilliseconds / 3600 / 1000);
	const durationMinutes = (durationInMilliseconds - durationHours * 3600 * 1000) / 60 / 1000;
	const durationDecimal = durationHours + Math.round(durationMinutes / .6) / 100;
	return formatDecimalNumber(durationDecimal);
}

export function formatDurationMinutes(durationInMilliseconds: number): string {
	const durationHours = wholeNumber(durationInMilliseconds / 3600 / 1000);
	const durationMinutes = Math.trunc(Math.abs((durationInMilliseconds - durationHours * 3600 * 1000) / 60 / 1000));
	return (durationInMilliseconds < 0 ? "-" : "") + Math.abs(durationHours) + ":" + (durationMinutes < 10 ? "0" : "") + durationMinutes;
}

/**
 useful for integer division

 wholeNumber(0.4) returns 0
 wholeNumber(1.7) returns 1
 wholeNumber(-0.4) returns 0
 wholeNumber(-1.7) returns -1

 (whereas Math.floor(-1.7) would return -2)
 */
export function wholeNumber(number: number): number {
	// tslint:disable-next-line:no-bitwise
	return ~~(number);
}

export function formatDuration(duration: number | Temporal.Duration, breakTime?: number, showPlus?: boolean): string {
	const durationMs = duration instanceof Temporal.Duration ? duration.total('milliseconds') : (duration ?? 0);
	const durationToFormat = durationMs - (breakTime ? Number(breakTime) * 60000 : 0);
	let displayedDuration = "" + durationToFormat;

	if (globals.durationFormat === DurationFormat.Decimal) {
		displayedDuration = formatDurationDecimal(durationToFormat);
	} else if (globals.durationFormat === DurationFormat.Minutes) {
		displayedDuration = formatDurationMinutes(durationToFormat);
	}
	if (showPlus && durationToFormat > 0) {
		displayedDuration = "+" + displayedDuration;
	}
	return displayedDuration;
}

export function formatDurationDays(durationInDays: number, showFractionalNumber?: boolean): string {
	if (typeof showFractionalNumber === "undefined") {
		showFractionalNumber = true;
	}
	const durationWholeNumber = wholeNumber(durationInDays);
	const fractionPart = durationInDays - durationWholeNumber;

	if (fractionPart === 0) {
		return durationWholeNumber + "";
	} else if (showFractionalNumber && (fractionPart === -0.5 || fractionPart === 0.5) && durationWholeNumber !== 0) {
		return durationWholeNumber + "½";
	} else if (showFractionalNumber && fractionPart === 0.5 && durationWholeNumber === 0) {
		return "½";
	} else if (showFractionalNumber && fractionPart === -0.5 && durationWholeNumber === 0) {
		return "-" + "½";
	} else {
		return formatDecimalNumber(durationInDays);
	}
}

/**
 * Calculates the duration between two dates provided as ISO strings
 *
 * @param startDateString Start time as ISO string
 * @param breakTime breakTime in minutes
 * @param endDateString End time as ISO string. Defaults to the current time if not provided
 *
 * @returns The duration between the two dates
 */
export function calculateDurationSince(startDateString: string, breakTime: number, endDateString?: string): Temporal.Duration {
	const startDate = parseISOZonedDateTime(startDateString);
	if (!startDate) return new Temporal.Duration();
	const endDt = parseISOZonedDateTime(endDateString) ?? Temporal.Now.zonedDateTimeISO();
	return startDate.until(endDt, {largestUnit: 'minutes'}).subtract({minutes: breakTime || 0});
}

/**
 * Calculates the duration between two dates provided as ISO strings
 *
 * @param startDateString Start time as ISO string
 * @param endDateString End time as ISO string
 * @param breakTime breakTime in minutes
 *
 * @returns The duration between the two dates
 */
export function calculateDurationBetween(startDateString: string, endDateString: string, breakTime: number): Temporal.Duration {
	// NOTE: parameter names are swapped — tracked as TIMR-12130
	const startDate = parseISOZonedDateTime(endDateString);
	const endDate = parseISOZonedDateTime(startDateString);
	return startDate !== null && endDate !== null
		? endDate.until(startDate, {largestUnit: 'minutes'}).subtract({minutes: breakTime || 0})
		: new Temporal.Duration();
}

/**
 * Calculates the duration between two dates provided as ISO strings
 *
 * @param startTimeString Start time as ISO string
 * @param endTimeString End time as ISO string
 *
 * @returns The duration between the two times
 */
export function calculateDurationBetweenTimes(startTimeString: string, endTimeString: string): Temporal.Duration {
	const startTime = parseTimeInput(startTimeString);
	const endTime = parseTimeInput(endTimeString);
	return startTime !== null && endTime !== null ? startTime.until(endTime) : new Temporal.Duration();
}

/**
 * Formats the running duration since the start of the running entry.
 * Break times and the status of the current entry are also taken into account
 *
 * @param runningEntry - The running entry containing the start time and status.
 * @returns The formatted duration since the start of the running entry.
 */
export function formatRunningDurationSince(runningEntry: RunningEntry): string {
	const breakTime = runningEntry.status === RunningTimeStatus.PAUSED ? calculateBreakTime(runningEntry) : (runningEntry.breakTime || 0);
	return formatDurationSince(runningEntry.start, breakTime, false);
}

// DEVNOTE: Note that in most cases the status (RunningTimeStatus) of a recording must be taken into account for the calculation
// 			formatRunningDurationSince() is possibly the function that should be used, as it takes into account
export function formatDurationSince(startDateString: string, breakTime: number, appendUnit?: boolean): string {
	const unit = typeof appendUnit === "undefined" || appendUnit ? " h" : "";
	return formatDuration(calculateDurationSince(startDateString, breakTime)) + unit;
}

export function formatCurrency(amount: number, currency?: string): string {
	return new Intl.NumberFormat(globals.decimalSeparator === "." ? "en" : "de", {
		style: "decimal",
		minimumFractionDigits: 2,
		maximumFractionDigits: 2
	}).format(amount) + " " + (currency || globals.currency);
}

export function formatDecimalNumber(decimalNumber: number, decimals: number = 2): string {
	const decimalNumberString = decimalNumber != null && typeof decimalNumber === "number" ? decimalNumber.toFixed(decimals) : "";
	return typeof globals !== "undefined" && typeof globals.decimalSeparator !== "undefined" ? decimalNumberString.replace(/\./g, globals?.decimalSeparator) : decimalNumberString;
}

export function parseDecimalNumber(numberString: string, defaultValue?: number): number {
	if (typeof defaultValue === "undefined") {
		defaultValue = 0;
	}
	if (typeof numberString === "string") {
		if (numberString.indexOf(",") !== -1) {
			numberString = numberString.replace(",", ".");
		}
		// remove thousand separators
		const lastIndex = numberString.lastIndexOf(".");
		if (lastIndex !== -1) {
			numberString = numberString.substr(0, lastIndex).replace(/\./g, "") + numberString.substr(lastIndex);
		}
	}
	const floatValue = Number.parseFloat(numberString);
	return Number.isNaN(floatValue) ? defaultValue : floatValue;
}

export function parseIntegerNumber(numberString: string): number {
	const intValue = Number.parseInt(numberString, 10);
	return Number.isNaN(intValue) ? 0 : intValue;
}

export function calculateBreakTime(runningRecording: RunningEntry): number {
	const pausedAt = parseISOZonedDateTime(runningRecording.pausedAt);
	if (!pausedAt) return runningRecording.breakTime ?? 0;
	const diffMinutes = Math.floor(pausedAt.until(Temporal.Now.zonedDateTimeISO()).total('minutes'));
	return (runningRecording.breakTime ?? 0) + diffMinutes;
}

/**
 * Returns the time zone ID of the browser
 */
export function getCurrentTimeZoneId() {
	return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * Inclusive day-granularity range check. Returns true when `date` falls within
 * `[from, to]`, with each moment formatted in its own zone — same rules as the
 * primitives above.
 */
export function isWithinDayRange(
	from: DayComparable,
	date: DayComparable,
	to: DayComparable,
): boolean {
	return isSameOrBeforeDay(from, date) && isSameOrBeforeDay(date, to);
}

export function parseDuration(durationString: string, minuteDetectionDisabled?: boolean): number {
	if (durationString === undefined || durationString === "") {
		return 0;
	}
	let duration = 0;
	durationString = durationString.trim();
	if (durationString.includes(":")) {
		const isNegativeDuration = durationString.substring(0, 1) === "-";
		const durationSplit = durationString.split(":");
		if (durationSplit.length > 0) {
			const hours = Number.parseInt(durationSplit[0], 10);
			duration = Number.isNaN(hours) ? 0 : hours * 60;
		}
		if (durationSplit.length > 1) {
			const minutes = Number.parseInt(durationSplit[1], 10);
			duration += Number.isNaN(minutes) ? 0 : minutes * (isNegativeDuration ? -1 : 1);
		}
	} else {
		if (durationString.includes(",")) {
			durationString = durationString.replace(",", ".");
		}
		duration = Number.parseFloat(durationString);
		if (duration < 12 || minuteDetectionDisabled) {
			duration = duration * 60;
		}
	}
	if (Number.isNaN(duration)) {
		duration = 0;
	}
	return Math.round(duration);
}

export function daysBetween(startDate?: Temporal.PlainDate | null, endDate?: Temporal.PlainDate | null): number {
	if (!startDate || !endDate) {
		return 0;
	}
	return startDate.until(endDate, {largestUnit: 'day'}).total('days');
}

export function parseVacation(vacation: string, vacationUnit: VacationUnit): number {
	if (vacationUnit === VacationUnit.Days) {
		return parseDecimalNumber(vacation);
	} else if (vacationUnit === VacationUnit.Minutes) {
		return parseDuration(vacation, true);
	}
	return 0;
}

export function formatVacation(vacation: number, vacationUnit: VacationUnit, showFractionalNumber?: boolean): string {
	if (vacationUnit === VacationUnit.Days) {
		return formatDurationDays(vacation, showFractionalNumber);
	} else if (vacationUnit === VacationUnit.Minutes) {
		return formatDuration((vacation || 0) * 60000);
	}
	return "";
}

const WEEKDAY_DATE_COMMA_DIVIDER = ", ";

/**
 * Formats the date the way a date range renders it: the weekday, then the formatted date, e.g. "Mon, 13.07.2026".
 */
export function formatDayWithDate(date: Temporal.PlainDate | Temporal.ZonedDateTime | null | undefined): string {
	if (!date) return "";
	return formatDateDay(date) + WEEKDAY_DATE_COMMA_DIVIDER + formatDate(date);
}

export function formatDateRange(startDate: Temporal.PlainDate | null, endDate: Temporal.PlainDate | null, startHalfDay: boolean = false, endHalfDay: boolean = false): string {
	if (startDate && endDate && !startDate.equals(endDate)) {
		return formatDayWithDate(startDate) + formatHalfDay(startHalfDay) + `<i class="time-separation-symbol ml-5 mr-5"></i>` +
			formatDayWithDate(endDate) + formatHalfDay(endHalfDay);
	}
	if (startDate) {
		return formatDayWithDate(startDate) + formatHalfDay(startHalfDay);
	}
	if (endDate) {
		return formatDayWithDate(endDate) + formatHalfDay(endHalfDay);
	}
	return "";
}

export function formatHalfDay(halfDay: boolean): string {
	return halfDay ? ` <span class="text-halfDay">${messages["durationUnit.HALF_DAYS"]}</span>` : "";
}

/**
 * Renders a date-time range (used for minute-based requests / date-time recordings). The timezone offset is
 * taken from the {@link Temporal.ZonedDateTime} operands and shown when the client shows time zones or when the
 * two endpoints differ. When {@code showDurationDays} is set only the start day is rendered (day-based display).
 */
export function formatDateTimeRange(start: Temporal.ZonedDateTime | null, end: Temporal.ZonedDateTime | null, changed?: boolean, showDurationDays?: boolean | null): string {
	const changedString = changed ? `<div class="edited-pen"><i class="fal fa-pen" title="${messages["report.column.changed.title"]}"></i></div>` : "";
	if (end) {
		const startTimezone = start?.offset ?? "";
		const endTimezone = end.offset;
		const timeZoneStringStartDate = (globals.showTimeZone || (startTimezone !== endTimezone)) ? startTimezone : "";
		const timeZoneStringEndDate = (globals.showTimeZone || (startTimezone !== endTimezone)) ? endTimezone : "";

		if (showDurationDays) {
			return `<div>
						${formatDateDay(start)}, <span class="text-size-small"> ${formatDate(start)}</span>
					</div>`;
		} else if (formatDate(start) === formatDate(end)) {
			return `<div class="flexbox-container">
						<div>
							<span>${formatTime(start)}</span>
							<span class="text-timezone"> ${timeZoneStringStartDate}</span>
							<i class="time-separation-symbol"></i>
							<span> ${formatTime(end)}
								<span class="text-timezone"> ${timeZoneStringEndDate} </span>
							</span>
							<div>
								${formatDateDay(start)}, <span class="text-size-small">${formatDate(start)}</span>
							</div>
						</div>
						${changedString}
					</div>`;
		} else {
			return `<div class="flexbox-container">
						<div>
							${formatTime(start)}<span class="text-timezone"> ${timeZoneStringStartDate}</span>
							<div>
								${formatDateDay(start)}, <span class="text-size-small">${formatDate(start)}</span>
							</div>
						</div>
						<div>
							<i class="time-separation-symbol"></i>
						</div>
						<div>
							${formatTime(end)}<span class="text-timezone"> ${timeZoneStringEndDate}</span>
							<div>
								${formatDateDay(end)}, <span class="text-size-small">${formatDate(end)}</span>
							</div>
						</div>
						${changedString}
					</div>`;
		}
	} else {
		const timeZoneStringRecordingStart = globals.showTimeZone ? start?.offset ?? "" : "";
		return `<div>
					<span>${formatTime(start)}</span>
					<span class="text-timezone"> ${timeZoneStringRecordingStart}</span>
					<div>
						${formatDateDay(start)}, <span class="text-size-small">${formatDate(start)}</span>
					</div>
				</div>`;
	}
}

/** Parses a `±HH:mm` UTC-offset string into signed minutes. Inverse of {@link formatTimeZone}. */
export function parseTimeZone(timezoneString: string): number {
	return (parseInt(timezoneString.substring(1, 3), 10) * 60 + parseInt(timezoneString.substring(4), 10)) * (timezoneString.substring(0, 1) === "+" ? 1 : -1);
}

/** Formats a UTC offset in minutes as `±HH:mm`. Inverse of {@link parseTimeZone}. */
export function formatTimeZone(offsetMinutes: number): string {
	const sign = offsetMinutes < 0 ? "-" : "+";
	const absoluteMinutes = Math.abs(offsetMinutes);
	const hours = Math.floor(absoluteMinutes / 60).toString().padStart(2, "0");
	const minutes = (absoluteMinutes % 60).toString().padStart(2, "0");
	return `${sign}${hours}:${minutes}`;
}

// ---- Day-granularity comparison helpers (Temporal-native) ----
//
// Temporal.PlainDate has built-in type-safe day comparison, so we can use
// the native API directly instead of string formatting. Mixed operands
// (PlainDate vs. ZonedDateTime) are handled by normalising to PlainDate first.

type DayComparable = Temporal.PlainDate | Temporal.ZonedDateTime | null | undefined;

function toPlainDate(v: DayComparable): Temporal.PlainDate | null {
	if (!v) return null;
	return v instanceof Temporal.ZonedDateTime ? v.toPlainDate() : v;
}

export function isSameDay(a: DayComparable, b: DayComparable): boolean {
	const da = toPlainDate(a);
	const db = toPlainDate(b);
	if (!da || !db) return false;
	return da.equals(db);
}

export function isBeforeDay(a: DayComparable, b: DayComparable): boolean {
	const da = toPlainDate(a);
	const db = toPlainDate(b);
	if (!da || !db) return false;
	return Temporal.PlainDate.compare(da, db) < 0;
}

export function isAfterDay(a: DayComparable, b: DayComparable): boolean {
	const da = toPlainDate(a);
	const db = toPlainDate(b);
	if (!da || !db) return false;
	return Temporal.PlainDate.compare(da, db) > 0;
}

export function isSameOrBeforeDay(a: DayComparable, b: DayComparable): boolean {
	const da = toPlainDate(a);
	const db = toPlainDate(b);
	if (!da || !db) return false;
	return Temporal.PlainDate.compare(da, db) <= 0;
}

export function isSameOrAfterDay(a: DayComparable, b: DayComparable): boolean {
	const da = toPlainDate(a);
	const db = toPlainDate(b);
	if (!da || !db) return false;
	return Temporal.PlainDate.compare(da, db) >= 0;
}
